import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { UIMessage } from 'ai';
import { DefaultChatTransport } from 'ai';
import { useChat } from '@ai-sdk/react';
import { motion, AnimatePresence } from 'framer-motion';
import { BatteryLow, ChevronDown, Sparkles } from 'lucide-react';
import { CSRF_COOKIE, readCookie } from '@/shared/api/client';
import { useBrand } from '@/shared/lib/brand';
import { cn } from '@/shared/lib/cn';
import { formatDate } from '@/shared/lib/format';
import { Button } from '@/shared/ui/button';
import { Tooltip } from '@/shared/ui/tooltip';
import { useMyAiCredits } from '@/features/ai-credits/api/use-my-ai-credits';
import { CreditsBar } from '@/features/ai-credits/ui/credits-bar';
import {
  CREDITS_EXHAUSTED_MESSAGE,
  isCreditsExhaustedError,
} from '@/features/ai-credits/model/credits-error';
import {
  Conversation,
  Message,
  MessageAvatar,
  MessageContent,
  Response,
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  Suggestions,
  Suggestion,
  Actions,
  Action,
  Tool,
  toolDisplayName,
} from '@/shared/ui/ai';
import type { SegmentationCriteria } from '@/shared/types/segmentation';
import { useSegmentationCanvasStore } from '../model/segmentation-canvas-store';

interface Props {
  idPassbook: string;
  segmentationId?: string;
  /** Agrupa o custo de IA desta conversa; o construtor manda o mesmo id ao salvar. */
  chatSessionId: string;
  /** Disparado quando a IA chama `segmentationReady` e o usuário clica em "Criar"/"Salvar". */
  onSegmentationReady?: () => void;
}

const TOOL_LABELS: Record<string, string> = {
  updateSegmentationDraft: 'Atualizar rascunho',
  askClarification: 'Perguntar',
  segmentationReady: 'Pronto para salvar',
  fetchLeadsPage: 'Buscar mais leads',
};

const HIDE_TEXT_TOOLS = new Set(['askClarification', 'updateSegmentationDraft', 'segmentationReady']);

const STARTER_PROMPTS = [
  'Leads inativos há mais de 90 dias',
  'Leads de uma cidade ou estado específico',
  'Leads com saldo alto que não compram há um tempo',
];

/**
 * Chat de IA como popup flutuante (abre/minimiza, fica por cima de tudo) —
 * cada `updateSegmentationDraft` que a IA chama é aplicado direto no
 * `segmentation-canvas-store` assim que a tool call termina; `askClarification`
 * vira chips de resposta rápida; `segmentationReady` vira um botão de ação
 * que dispara `onSegmentationReady` — só ali é que algo é salvo de verdade
 * no backend (tudo antes disso é rascunho local, descartável).
 */
export function SegmentationChatWidget({ idPassbook, segmentationId, chatSessionId, onSegmentationReady }: Props) {
  const [open, setOpen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true,
  );
  const [input, setInput] = useState('');
  const applyCriteria = useSegmentationCanvasStore((s) => s.applyCriteria);
  const appliedToolCallIds = useRef(new Set<string>());
  const { brand } = useBrand();
  const brandIcon = brand.assets.icon.brand;

  const { messages, sendMessage, status, error } = useChat({
    transport: new DefaultChatTransport({
      api: '/bff/segmentation-chat',
      headers: (): Record<string, string> => {
        const csrf = readCookie(CSRF_COOKIE);
        const headers: Record<string, string> = {};
        if (csrf) headers['X-CSRF-Token'] = csrf;
        return headers;
      },
      body: () => {
        const { groups, connectors, channel } = useSegmentationCanvasStore.getState();
        return { idPassbook, segmentationId, chatSessionId, channel, currentCriteria: { groups, connectors } };
      },
    }),
  });

  const isThinking = status === 'submitted' || status === 'streaming';
  const { data: credits, refetch: refetchCredits } = useMyAiCredits(idPassbook);
  const creditsBlocked = Boolean(credits?.exhausted) || isCreditsExhaustedError(error);

  // Cada resposta consome créditos: atualiza a barra quando o assistente termina.
  const previousStatus = useRef(status);
  useEffect(() => {
    const finished = previousStatus.current !== status && (status === 'ready' || status === 'error');
    previousStatus.current = status;
    if (finished) void refetchCredits();
  }, [status, refetchCredits]);

  useEffect(() => {
    for (const message of messages) {
      for (const part of message.parts as ChatPart[]) {
        if (partToolName(part) !== 'updateSegmentationDraft' || part.state !== 'output-available') continue;
        if (!part.toolCallId || appliedToolCallIds.current.has(part.toolCallId)) continue;
        appliedToolCallIds.current.add(part.toolCallId);
        const output = part.output as { applied?: boolean; criteria?: SegmentationCriteria } | undefined;
        if (output?.applied && output.criteria) applyCriteria(output.criteria);
      }
    }
  }, [messages, applyCriteria]);

  function submit(e?: FormEvent, textOverride?: string) {
    e?.preventDefault();
    const text = (textOverride ?? input).trim();
    if (!text || creditsBlocked) return;
    void sendMessage({ text });
    setInput('');
  }

  return (
    <>
      <AnimatePresence>
        {!open ? (
          <motion.button
            key="trigger"
            type="button"
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            onClick={() => setOpen(true)}
            aria-label="Abrir chat de IA"
            className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-50 grid size-14 place-items-center rounded-full border-2 border-primary/40 bg-background shadow-glass-lg sm:bottom-6 sm:right-6 sm:size-16"
          >
            <img src={brandIcon} alt="" className="size-8 object-contain sm:size-9" />
          </motion.button>
        ) : (
          <motion.div
            key="panel"
            initial={{ scale: 0.9, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 24 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className="glass-elevated fixed inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] top-[4.75rem] z-50 flex flex-col overflow-hidden overscroll-contain rounded-2xl shadow-glass-lg md:inset-auto md:bottom-6 md:right-6 md:top-auto md:h-[min(680px,calc(100dvh-3rem))] md:w-[min(420px,calc(100vw-3rem))]"
          >
            <header className="flex shrink-0 items-center gap-2.5 border-b border-border/60 px-4 py-3">
              <span className="grid size-9 place-items-center rounded-full border border-border/70 bg-primary/10">
                <img src={brandIcon} alt="" className="size-5 object-contain" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">Assistente de audiência</p>
                <p className="truncate text-xs text-muted-foreground">
                  {creditsBlocked ? 'Sem créditos de IA' : isThinking ? 'Trabalhando…' : 'Pronto para ajudar'}
                </p>
              </div>
              {credits ? (
                <Tooltip
                  content={
                    credits.renewMonthly && credits.renewsAt
                      ? `Créditos de IA usados. Renova em ${formatDate(credits.renewsAt)}.`
                      : 'Créditos de IA usados.'
                  }
                >
                  <div className="w-20 shrink-0">
                    <CreditsBar percentUsed={credits.percentUsed} exhausted={creditsBlocked} />
                  </div>
                </Tooltip>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => setOpen(false)}
                aria-label="Minimizar chat"
              >
                <ChevronDown className="size-4" />
              </Button>
            </header>

            <Conversation className="flex-1" autoScrollDeps={[messages, status]}>
              {messages.length === 0 ? (
                <div className="flex flex-1 flex-col items-center gap-4 px-2 py-6 text-center">
                  <span className="grid size-16 place-items-center rounded-full border-2 border-primary/30 bg-primary/10">
                    <img src={brandIcon} alt="" className="size-8 object-contain" />
                  </span>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">Vamos montar sua segmentação? 👋</p>
                    <p className="text-sm text-muted-foreground">
                      Me conta que público você quer alcançar. Eu te pergunto as opções e a gente monta os grupos juntos.
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-2">
                    {STARTER_PROMPTS.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        onClick={() => submit(undefined, prompt)}
                        className={cn(
                          'flex items-center gap-2.5 rounded-xl border border-border/70 bg-[hsl(var(--glass-bg)/0.5)] px-3.5 py-2.5 text-left text-sm',
                          'shadow-glass-sm backdrop-blur-sm transition-colors hover:border-primary/40 hover:bg-primary/5',
                        )}
                      >
                        <Sparkles className="size-3.5 shrink-0 text-primary" />
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message, index) => {
                  const parts = message.parts as ChatPart[];
                  const prev = messages[index - 1];
                  const prevParts = (prev?.parts ?? []) as ChatPart[];
                  const hasTools = parts.some((part) => partToolName(part) != null);
                  const prevHadTerminalTools =
                    prev?.role === 'assistant' &&
                    prevParts.some((part) => {
                      const name = partToolName(part);
                      return name != null && HIDE_TEXT_TOOLS.has(name);
                    });
                  const isTextOnlyAssistant = message.role === 'assistant' && !hasTools;
                  if (isTextOnlyAssistant && prevHadTerminalTools) return null;
                  const thisText = mergedAssistantText(parts);
                  const prevText = mergedAssistantText(prevParts);
                  if (isTextOnlyAssistant && thisText && prev?.role === 'assistant' && thisText === prevText) {
                    return null;
                  }
                  return (
                    <ChatMessageView
                      key={message.id}
                      message={message}
                      brandIcon={brandIcon}
                      onSuggestionClick={(text) => submit(undefined, text)}
                      onReady={onSegmentationReady}
                    />
                  );
                })
              )}
              {isThinking ? <ThinkingIndicator /> : null}
            </Conversation>

            {error && !creditsBlocked ? (
              <p className="mx-3 mb-2 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                Não consegui responder agora. Tente enviar de novo em instantes.
              </p>
            ) : null}

            {creditsBlocked ? (
              <CreditsExhaustedNotice renewsAt={credits?.renewMonthly ? credits.renewsAt : null} />
            ) : (
              <PromptInput onSubmit={submit} className="m-3 mt-0 shrink-0">
                <PromptInputTextarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onSubmitShortcut={submit}
                  placeholder="Descreva o público que você quer segmentar…"
                />
                <PromptInputSubmit status={status} />
              </PromptInput>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function CreditsExhaustedNotice({ renewsAt }: { renewsAt: string | null }) {
  return (
    <div className="m-3 mt-0 flex shrink-0 items-start gap-3 rounded-2xl border border-warning/30 bg-warning/10 p-3.5">
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-warning/15 text-warning">
        <BatteryLow className="size-5" />
      </span>
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-semibold">Créditos de IA esgotados</p>
        <p className="text-sm text-muted-foreground">{CREDITS_EXHAUSTED_MESSAGE}</p>
        {renewsAt ? (
          <p className="text-xs text-muted-foreground">Seus créditos renovam em {formatDate(renewsAt)}.</p>
        ) : null}
        <p className="text-xs text-muted-foreground">Você ainda pode editar e salvar a segmentação no canvas.</p>
      </div>
    </div>
  );
}

function ThinkingIndicator() {
  const { brand } = useBrand();
  return (
    <div className="flex items-center gap-2 px-1 py-1">
      <motion.img
        src={brand.assets.icon.brand}
        alt=""
        className="size-5"
        animate={{ scale: [1, 1.2, 1], opacity: [0.55, 1, 0.55] }}
        transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <span className="text-xs text-muted-foreground">Trabalhando…</span>
    </div>
  );
}

// Tipos de part relevantes -- casts defensivos porque o generic UI_MESSAGE do
// useChat não está fechado com as tools do servidor (elas vivem só no BFF).
interface ChatPart {
  type: string;
  text?: string;
  toolCallId?: string;
  toolName?: string;
  state?: string;
  input?: unknown;
  output?: unknown;
}

function partToolName(part: ChatPart): string | null {
  if (part.type.startsWith('tool-')) return part.type.slice('tool-'.length);
  if (part.type === 'dynamic-tool' && typeof part.toolName === 'string' && part.toolName) {
    return part.toolName;
  }
  return null;
}

function mergedAssistantText(parts: ChatPart[]): string {
  const texts = parts
    .filter((part) => part.type === 'text' && part.text?.trim())
    .map((part) => part.text!.trim());
  return texts.filter((text, index) => text !== texts[index - 1]).join('\n\n');
}

interface ChatMessageViewProps {
  message: UIMessage;
  brandIcon: string;
  onSuggestionClick: (text: string) => void;
  onReady?: () => void;
}

function ChatMessageView({ message, brandIcon, onSuggestionClick, onReady }: ChatMessageViewProps) {
  const role = message.role === 'user' ? 'user' : 'assistant';
  const parts = message.parts as ChatPart[];
  const hasTools = parts.some((part) => partToolName(part) != null);
  const hasTerminalTools = parts.some((part) => {
    const name = partToolName(part);
    return name != null && HIDE_TEXT_TOOLS.has(name);
  });
  const text = mergedAssistantText(parts);
  const hasVisibleContent = Boolean(text && !hasTerminalTools) || hasTools;
  if (!hasVisibleContent) return null;

  return (
    <Message role={role}>
      <MessageAvatar role={role} src={role === 'assistant' ? brandIcon : undefined} />
      <div className="flex w-fit min-w-0 max-w-[85%] flex-col gap-2">
        {text && !hasTerminalTools ? (
          <MessageContent role={role}>
            <Response>{text}</Response>
          </MessageContent>
        ) : null}

        {parts.map((part, index) => {
          const toolKey = partToolName(part);
          if (!toolKey) return null;

          const output = part.output as Record<string, unknown> | undefined;

          return (
            <Tool
              key={part.toolCallId ?? index}
              name={toolDisplayName(`tool-${toolKey}`, TOOL_LABELS)}
              state={part.state}
            >
              {toolKey === 'askClarification' && part.state === 'output-available' ? (
                <>
                  {typeof output?.question === 'string' ? (
                    <p className="mb-2 text-sm">{output.question}</p>
                  ) : null}
                  {Array.isArray(output?.options) ? (
                    <Suggestions>
                      {(output.options as string[]).map((option) => (
                        <Suggestion
                          key={option}
                          suggestion={option}
                          onClick={() => onSuggestionClick(option)}
                        />
                      ))}
                    </Suggestions>
                  ) : null}
                </>
              ) : null}

              {toolKey === 'updateSegmentationDraft' ? (
                <p className="text-sm text-muted-foreground">
                  {typeof output?.error === 'string'
                    ? `Não consegui aplicar: ${output.error}`
                    : [
                        typeof output?.summary === 'string' ? output.summary : null,
                        typeof output?.leadCount === 'number'
                          ? `${Number(output.leadCount).toLocaleString('pt-BR')} leads`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') ||
                      (part.state === 'output-available'
                        ? 'Rascunho atualizado no canvas.'
                        : 'Aplicando no canvas…')}
                </p>
              ) : null}

              {toolKey === 'segmentationReady' && part.state === 'output-available' ? (
                <div className="flex flex-col gap-2">
                  {typeof output?.summary === 'string' ? (
                    <p className="text-sm">{output.summary}</p>
                  ) : null}
                  <Actions>
                    <Action onClick={onReady}>Criar/Salvar segmento</Action>
                  </Actions>
                </div>
              ) : null}

              {toolKey === 'fetchLeadsPage' && part.state === 'output-available' ? (
                <p className="text-sm text-muted-foreground">
                  {typeof output?.error === 'string'
                    ? output.error
                    : [
                        typeof output?.page === 'number' ? `Página ${String(output.page)}` : null,
                        typeof output?.rowCount === 'number'
                          ? `${Number(output.rowCount).toLocaleString('pt-BR')} leads`
                          : null,
                        output?.hasMore ? 'tem mais' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Concluído.'}
                </p>
              ) : null}

              {/* Tools futuras: o header do harness já basta; output JSON só se não houver UI própria. */}
              {toolKey !== 'askClarification' &&
              toolKey !== 'updateSegmentationDraft' &&
              toolKey !== 'segmentationReady' &&
              toolKey !== 'fetchLeadsPage' &&
              part.state === 'output-available' &&
              output ? (
                <p className="text-xs text-muted-foreground">Concluído.</p>
              ) : null}
            </Tool>
          );
        })}
      </div>
    </Message>
  );
}
